'use client';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, MotionDiv } from '@lib/motion';
import { QuestionProjection } from '@lib/sanity/queries/modules/faq/types';
import PortableText from '@ui/Text/PortableText';
import Text, { TextVariant } from '@ui/Text/Text';
import cn from '@utils/cn';
import React from 'react';

type Props = QuestionProjection & {
  isOpen: boolean;
  setOnClick: () => void;
  className?: string;
};

const AnimatedWrapper = ({ children, noIntial }: { children?: React.ReactNode; noIntial?: boolean }) => {
  return (
    <MotionDiv
      animate={{
        height: 'auto',
        opacity: 1,
        transition: {
          height: {
            duration: 0.4,
          },
          opacity: {
            duration: 0.25,
            delay: 0.15,
          },
        },
      }}
      exit={{
        height: 0,
        opacity: 0,
        transition: {
          height: {
            duration: 0.4,
          },
          opacity: {
            duration: 0.1,
          },
        },
      }}
      initial={noIntial ? {} : { height: 0, opacity: 0 }}
    >
      {children}
    </MotionDiv>
  );
};

const Item = ({ question, answerRich, answer, isOpen, setOnClick, className }: Props) => {
  const toggleAccordion = () => {
    setOnClick();
  };
  const hasAnswerRich =
    Array.isArray(answerRich?.value) &&
    answerRich?.value?.length > 0 &&
    Array.isArray(answerRich?.value?.[0]?.children) &&
    answerRich?.value?.[0]?.children?.length > 0 &&
    typeof answerRich?.value?.[0]?.children?.[0]?.text === 'string' &&
    answerRich?.value?.[0]?.children?.[0]?.text?.length > 0;

  return (
    <div
      className={cn(className, 'flex flex-col gap-4', {
        'hover:bg-input cursor-pointer': !isOpen,
      })}
      onClick={() => !isOpen && toggleAccordion()}
    >
      <div
        className={cn('text-foreground  flex  items-center', {
          'cursor-pointer': isOpen,
        })}
        onClick={() => isOpen && toggleAccordion()}
      >
        <Text content={question?.value} variant={TextVariant.SubtitleLG} className="w-full" />
        {!isOpen && (
          <AnimatedWrapper noIntial>
            <PlusIcon className="h-6 w-6" />
          </AnimatedWrapper>
        )}
        {isOpen && (
          <AnimatedWrapper noIntial>
            <MinusIcon className="h-6 w-6" />
          </AnimatedWrapper>
        )}
      </div>

      <AnimatePresence>
        {isOpen && hasAnswerRich && (
          <AnimatedWrapper>
            <PortableText content={answerRich} variant={TextVariant.TextBodyMD} />
          </AnimatedWrapper>
        )}
        {isOpen && answer && answer.value && !hasAnswerRich && (
          <AnimatedWrapper>
            <Text content={answer.value} variant={TextVariant.TextBodyMD} />
          </AnimatedWrapper>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Item;
